import React from "react";
import { SlidersHorizontal } from "lucide-react";
import type { InvestorAlphaPreference } from "../../types/investorAlpha";
import type { AlphaProgressResponses } from "./alphaProgressResponses";
import { getRiskProfileAnswer } from "./alphaRiskProfile";

const PREFERENCE_LABELS: Record<InvestorAlphaPreference, string> = {
  growth: "Maximize long-term growth",
  balance: "Balance growth and protection",
  protect: "Conservative",
};

export interface AlphaPreferencesCardProps {
  progressResponses: AlphaProgressResponses | null;
  onUpdate?: () => void;
  onStartSetup?: () => void;
  variant?: "inline" | "tile";
  displayOnly?: boolean;
  requestFailed?: boolean;
  onRetry?: () => void;
}

const HEADING_ID = "alpha-portfolio-preferences-heading";

const AlphaPreferencesCard: React.FC<AlphaPreferencesCardProps> = ({
  progressResponses,
  onUpdate,
  onStartSetup,
  variant = "inline",
  displayOnly = false,
  requestFailed = false,
  onRetry,
}) => {
  const objectivePreference = progressResponses?.investmentTrackPreference ?? null;
  const objectiveRiskProfile = objectivePreference !== null
    && progressResponses?.riskProfile?.investmentTrack === objectivePreference
    ? progressResponses.riskProfile
    : null;
  const hasAnswers = objectivePreference !== null && objectiveRiskProfile !== null;

  if (requestFailed) {
    return (
      <section aria-labelledby={HEADING_ID} className={variant === "tile" ? "flex h-full min-h-[280px] flex-col rounded-v7-panel border border-v7-ink/15 bg-v7-white p-5 md:p-6" : undefined}>
        <h2 id={HEADING_ID} className="text-base font-semibold text-v7-ink">
          Preferences unavailable
        </h2>
        <p className="mt-2 text-sm text-v7-ink-soft">
          We could not load this customer's portfolio preferences.
        </p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-4 inline-flex min-h-11 items-center justify-center rounded-v7-control bg-v7-ink px-4 text-sm font-medium text-v7-white"
          >
            Retry
          </button>
        )}
      </section>
    );
  }

  if (variant === "tile") {
    return (
      <section
        aria-labelledby={HEADING_ID}
        className="flex h-full min-h-[280px] flex-col rounded-v7-panel border border-v7-ink/15 bg-v7-white p-5 transition-colors duration-300 hover:bg-v7-paper md:p-6"
      >
        <div className="flex flex-1 flex-col">
          <div className="mb-5 flex items-start gap-3">
            <span
              aria-hidden="true"
              className="grid h-10 w-10 shrink-0 place-items-center rounded-v7-control bg-v7-paper text-v7-bronze-dark"
            >
              <SlidersHorizontal className="h-5 w-5" />
            </span>
            <div>
              <h2 id={HEADING_ID} className="text-base font-bold leading-tight text-v7-ink">
                Portfolio preferences
              </h2>
              <p className="mt-1 text-sm text-v7-ink-soft">
                Objectives and risk profile
              </p>
            </div>
          </div>

          {objectivePreference && objectiveRiskProfile ? (
            <dl className="divide-y divide-v7-ink/10 border-t border-v7-ink/10">
              <div className="flex items-center justify-between gap-5 py-3">
                <dt className="text-[10px] font-medium uppercase tracking-[0.08em] text-v7-ink/50">Objective</dt>
                <dd className="text-right font-v7-serif text-base text-v7-ink">
                  {PREFERENCE_LABELS[objectivePreference]}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-5 py-3">
                <dt className="text-[10px] font-medium uppercase tracking-[0.08em] text-v7-ink/50">Investment experience</dt>
                <dd className="max-w-[60%] text-right font-v7-serif text-base text-v7-ink">
                  {getRiskProfileAnswer("investmentExperience", objectiveRiskProfile)}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-5 py-3">
                <dt className="text-[10px] font-medium uppercase tracking-[0.08em] text-v7-ink/50">Time horizon</dt>
                <dd className="text-right font-v7-serif text-base text-v7-ink">
                  {getRiskProfileAnswer("timeHorizon", objectiveRiskProfile)}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-5 py-3">
                <dt className="text-[10px] font-medium uppercase tracking-[0.08em] text-v7-ink/50">Overall risk score</dt>
                <dd className="font-v7-serif text-2xl text-v7-ink">
                  {objectiveRiskProfile.riskScore} / 10
                </dd>
              </div>
            </dl>
          ) : (
            <div className="flex flex-1 flex-col justify-center border-t border-v7-ink/10 pt-4">
              <p className="text-base font-medium text-v7-ink">We need more information</p>
              <p className="mt-1 text-sm text-v7-ink-soft">Answer a few questions about your objectives and risk profile.</p>
            </div>
          )}
        </div>

        <div className="mt-5 flex items-end">
          {displayOnly ? (
            <span className="text-xs font-semibold uppercase tracking-wider text-v7-ink/50">
              Read-only
            </span>
          ) : (
            <button
              type="button"
              onClick={hasAnswers ? onUpdate : onStartSetup}
              className="inline-flex min-h-11 items-center justify-center rounded-v7-control bg-v7-ink px-4 text-sm font-medium text-v7-white transition-colors hover:bg-v7-ink-soft"
            >
              {hasAnswers ? "Update" : "Complete profile"}
            </button>
          )}
        </div>
      </section>
    );
  }

  return (
    <section aria-labelledby={HEADING_ID}>
      <h2 id={HEADING_ID} className="text-base font-semibold text-v7-ink">
        Portfolio preferences
      </h2>
      <div className="mt-4 overflow-hidden rounded-v7-panel border border-v7-ink/20 bg-v7-white">
        {objectivePreference && objectiveRiskProfile ? (
          <dl className="divide-y divide-v7-ink/15">
            <div className="flex items-center justify-between gap-5 px-5 py-4">
              <dt className="text-[10px] font-medium uppercase tracking-[0.08em] text-v7-ink/50">Objective</dt>
              <dd className="text-right font-v7-serif text-base text-v7-ink">
                {PREFERENCE_LABELS[objectivePreference]}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-5 px-5 py-4">
              <dt className="text-[10px] font-medium uppercase tracking-[0.08em] text-v7-ink/50">Investment experience</dt>
              <dd className="max-w-[65%] text-right font-v7-serif text-base text-v7-ink">
                {getRiskProfileAnswer("investmentExperience", objectiveRiskProfile)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-5 px-5 py-4">
              <dt className="text-[10px] font-medium uppercase tracking-[0.08em] text-v7-ink/50">Time horizon</dt>
              <dd className="text-right font-v7-serif text-base text-v7-ink">
                {getRiskProfileAnswer("timeHorizon", objectiveRiskProfile)}
              </dd>
            </div>
            <div className="px-5 py-4">
              <dt className="text-[10px] font-medium uppercase tracking-[0.08em] text-v7-ink/50">Overall risk score</dt>
              <dd className="mt-2 flex items-end justify-between gap-5">
                <span className="font-v7-serif text-2xl text-v7-ink">
                  {objectiveRiskProfile.riskScore} / 10
                </span>
                {displayOnly ? (
                  <span className="text-xs font-semibold uppercase tracking-wider text-v7-ink/50">
                    Read-only
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={onUpdate}
                    className="min-h-11 rounded-v7-control bg-v7-ink px-4 text-sm font-medium text-v7-white transition-colors hover:bg-v7-ink-soft"
                  >
                    Update
                  </button>
                )}
              </dd>
            </div>
          </dl>
        ) : (
          <div className="flex items-end justify-between gap-5 p-5">
            <div>
              <p className="text-base font-medium text-v7-ink">We need more information</p>
              <p className="mt-1 text-sm text-v7-ink-soft">Answer a few questions about your objectives and risk profile.</p>
            </div>
            {displayOnly ? (
              <span className="text-xs font-semibold uppercase tracking-wider text-v7-ink/50">
                Read-only
              </span>
            ) : (
              <button
                type="button"
                onClick={onStartSetup}
                className="min-h-11 shrink-0 rounded-v7-control bg-v7-ink px-4 text-sm font-medium text-v7-white transition-colors hover:bg-v7-ink-soft"
              >
                Complete profile
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default AlphaPreferencesCard;
